import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import SEO from '../components/SEO';
import { CheckCircle, Clock, Book, Code, Award, ArrowRight } from 'lucide-react';

const DevOpsEngineerPath = () => {
  const [expandedModule, setExpandedModule] = useState(null);

  const learningPath = {
    title: "DevOps Engineer",
    description: "Master the art of building CI/CD pipelines, infrastructure automation, and DevOps practices. Learn to streamline development workflows, implement container orchestration, and manage cloud infrastructure at scale.",
    duration: "12-16 weeks",
    difficulty: "Advanced",
    outcomes: [
      "Build and manage CI/CD pipelines for automated deployments",
      "Master Infrastructure as Code with Terraform and CloudFormation",
      "Implement container orchestration with Kubernetes",
      "Design monitoring, logging, and alerting systems"
    ],
    modules: [
      {
        id: 1,
        title: "Career Preparation",
        duration: "1-2 weeks",
        description: "Master interview skills and career strategies for DevOps roles",
        courses: [
          "AWS Interview Mastery (Get Hired!)"
        ],
        skills: ["Technical Interviews", "System Design", "Behavioral Questions"],
        projects: ["Practice mock interviews", "Build your DevOps portfolio"]
      },
      {
        id: 2,
        title: "Cloud Operations Fundamentals",
        duration: "3-4 weeks",
        description: "Build strong CloudOps and monitoring foundations",
        courses: [
          "Study Notes: AWS Certified Cloud Practitioner",
          "Study Notes: AWS Certified CloudOps Engineer - Associate",
          "AWS Certified Cloud Practitioner Practice Tests"
        ],
        skills: ["Cloud Operations", "Monitoring & Logging", "Infrastructure Management"],
        projects: ["Setup monitoring dashboards", "Implement alerting systems"]
      },
      {
        id: 3,
        title: "Hands-On Projects",
        duration: "4-5 weeks",
        description: "Build production-ready DevOps skills through practical projects",
        courses: [
          "Build: 5 Beginner AWS Cloud Projects",
          "Build: 5 Intermediate AWS Cloud Projects"
        ],
        skills: ["CI/CD Pipelines", "Infrastructure as Code", "Automation"],
        projects: ["Build automated deployment pipelines", "Configure scalable infrastructure"]
      },
      {
        id: 4,
        title: "Advanced DevOps Engineering",
        duration: "4-5 weeks",
        description: "Master advanced DevOps practices, tools, and enterprise patterns",
        courses: [
          "5 AWS Cloud Projects to Become a DevOps Engineer"
        ],
        skills: ["Kubernetes", "Terraform", "Ansible", "Monitoring & Alerting"],
        projects: ["Container orchestration", "Configuration management", "Production monitoring"]
      }
    ],
    certifications: [
      "AWS Certified Cloud Practitioner (Foundation)",
      "AWS Certified SysOps Administrator (Recommended Next)"
    ],
    careerOutlook: {
      averageSalary: "$90,000 - $150,000",
      jobGrowth: "22% annually",
      topSkills: ["CI/CD", "Kubernetes", "Terraform", "AWS", "Docker"]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={`${learningPath.title} Learning Path | Zero To Cloud`}
        description={learningPath.description}
        keywords="DevOps engineer, AWS DevOps, CI/CD, Kubernetes, Terraform, Infrastructure as Code"
      />
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-50 to-red-100">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award size={16} />
              Career Learning Path
            </div>
            <div className="text-6xl mb-6">🚀</div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {learningPath.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {learningPath.description}
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-sm">
                <Clock size={20} className="text-orange-500" />
                <span className="font-medium">{learningPath.duration}</span>
              </div>
            </div>
            
            <a 
              href="https://learn.zerotocloud.co/p/learning-path-devops-engineer"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
            >
              Start Learning Path
            </a>
          </div>
        </div>
      </section>

      {/* What is a DevOps Engineer */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                What is a DevOps Engineer?
              </h2>
              <div className="prose prose-lg mx-auto text-center">
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A DevOps Engineer bridges the gap between development and operations teams, automating and streamlining the software delivery process. They design CI/CD pipelines, manage infrastructure as code, and ensure reliable, scalable deployments.
                </p>
                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Daily Responsibilities</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                        Build and maintain CI/CD pipelines
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                        Automate infrastructure provisioning
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                        Monitor system performance and reliability
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                        Implement security best practices
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Work Environment</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Cross-functional team collaboration
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Fast-paced, agile environments
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        Continuous learning and improvement
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        High-impact automation projects
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {learningPath.outcomes.map((outcome, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Learning Modules */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Learning Modules
            </h2>
            
            <div className="space-y-6">
              {learningPath.modules.map((module, index) => (
                <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedModule(expandedModule === module.id ? null : module.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-lg flex items-center justify-center font-bold text-lg">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">
                            {module.title}
                          </h3>
                          <p className="text-gray-600 text-sm">{module.duration}</p>
                        </div>
                      </div>
                      <ArrowRight 
                        size={20} 
                        className={`text-gray-400 transition-transform ${
                          expandedModule === module.id ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                    <p className="text-gray-600 mt-4">{module.description}</p>
                  </div>
                  
                  {expandedModule === module.id && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Book size={16} />
                            Courses
                          </h4>
                          <ul className="space-y-2">
                            {module.courses.map((course, i) => (
                              <li key={i} className="text-gray-600 text-sm flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                {course}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Code size={16} />
                            Key Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {module.skills.map((skill, i) => (
                              <span 
                                key={i} 
                                className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 mb-3">Projects</h4>
                        <ul className="space-y-1">
                          {module.projects.map((project, i) => (
                            <li key={i} className="text-gray-600 text-sm flex items-center gap-2">
                              <CheckCircle size={14} className="text-green-500" />
                              {project}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Get Started Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Ready to get started?
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Side - Learning Path Info */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    🎯 Learning Path Overview
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-orange-500" />
                      <span className="text-gray-700">
                        <strong>Duration:</strong> {learningPath.duration}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Book size={20} className="text-green-500" />
                      <span className="text-gray-700">
                        <strong>Total Courses:</strong> 7 comprehensive courses
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award size={20} className="text-orange-500" />
                      <span className="text-gray-700">
                        <strong>Certifications:</strong> Cloud Practitioner + CloudOps
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle size={20} className="text-teal-500" />
                      <span className="text-gray-700">
                        <strong>Projects:</strong> 15+ hands-on projects
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    📈 What You'll Achieve
                  </h3>
                  <ul className="space-y-3">
                    {learningPath.outcomes.map((outcome, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                        <span className="text-gray-700 text-sm">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Side - Course Thumbnails with Scroll */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                  📚 Courses Included
                </h3>
                
                <div className="h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 space-y-2 pr-2">
                  {/* Course 1 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/1.png" 
                        alt="AWS Interview Mastery"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              AWS Interview Mastery (Get Hired!)
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$129</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 2 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/4.png" 
                        alt="Cloud Practitioner Notes"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Study Notes: AWS Certified Cloud Practitioner
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$29</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 3 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/study-notes-cloudops.png" 
                        alt="CloudOps Notes"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Study Notes: AWS Certified CloudOps Engineer - Associate
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$39</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 4 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/practice-exams-cloud-practitioner.png" 
                        alt="Practice Tests"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              AWS Certified Cloud Practitioner Practice Tests
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$29</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 5 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/2.png" 
                        alt="Beginner Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Build: 5 Beginner AWS Cloud Projects
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$69</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 6 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/3.png" 
                        alt="Intermediate Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              Build: 5 Intermediate AWS Cloud Projects
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$69</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course 7 */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <img 
                        src="/course-covers/devops-engineer-projects.png" 
                        alt="DevOps Projects"
                        className="w-16 h-16 object-contain bg-gray-50 rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              5 AWS Cloud Projects to Become a DevOps Engineer
                            </h4>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">$69</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pricing Section at Bottom */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Ready to get started?
            </h2>
            
            {/* Centered Pricing */}
            <div className="max-w-md mx-auto">
              <div className="text-center space-y-4">
                <a 
                  href="https://learn.zerotocloud.co/p/learning-path-devops-engineer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Start Learning Path - $299
                </a>
                <div className="text-sm text-gray-500 font-medium">Complete Learning Path</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default DevOpsEngineerPath;
